function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import noop from "lodash/noop";
import { useMemo } from "react";
import { Platform } from "react-native";
import RNPickerSelect from "react-native-picker-select";
import { Icon } from "./Icon";
import { getStyles } from "./TextInput";
import { AccessibleInput } from "../hooks/useAccessibleInput";
import { useTheme } from "../styles";
/**
 * A dropdown picker input field that allows the user to select an options from
 * a provided list.
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-109}
 */ export function PickerInput(_param) {
    var ariaLabel = _param['aria-label'], error = _param.error, label = _param.label, labelHorizontal = _param.labelHorizontal, onChangeValue = _param.onChangeValue, placeholder = _param.placeholder, style = _param.style, testID = _param.testID, variant = _param.variant, required = _param.required, disabled = _param.disabled, props = _object_without_properties(_param, [
        'aria-label',
        "error",
        "label",
        "labelHorizontal",
        "onChangeValue",
        "placeholder",
        "style",
        "testID",
        "variant",
        "required",
        "disabled"
    ]);
    var theme = useTheme().theme;
    var baseInputStyle = getStyles(theme)[variant === 'flat' ? 'flat' : 'outlined'];
    var inputStyle = error ? _object_spread_props(_object_spread({}, baseInputStyle), {
        borderColor: theme.color.danger
    }) : baseInputStyle;
    var selectedLabel = useMemo(function() {
        var _props_items_find;
        return (_props_items_find = props.items.find(function(item) {
            return item.value === props.value;
        })) === null || _props_items_find === void 0 ? void 0 : _props_items_find.label;
    }, [
        props.items,
        props.value
    ]);
    var items = useMemo(function() {
        return props.items.map(function(i) {
            var _i_value;
            return _object_spread_props(_object_spread({}, i), {
                key: (_i_value = i.value) === null || _i_value === void 0 ? void 0 : _i_value.toString()
            });
        });
    }, [
        props.items
    ]);
    return /*#__PURE__*/ _jsx(AccessibleInput, {
        "aria-label": ariaLabel,
        error: error,
        label: label,
        labelHorizontal: labelHorizontal,
        style: style,
        testID: testID,
        required: required,
        disabled: disabled,
        children: function(accessibleProps) {
            var _accessibleProps_arialabel;
            return /*#__PURE__*/ _jsx(RNPickerSelect, _object_spread_props(_object_spread({}, props), {
                items: items,
                pickerProps: {
                    testID: testID,
                    'aria-label': accessibleProps['aria-label'],
                    'aria-disabled': disabled
                },
                useNativeAndroidPickerStyle: false,
                touchableWrapperProps: _object_spread_props(_object_spread({}, accessibleProps), {
                    accessible: true,
                    'aria-label': "Dropdown list. ".concat(selectedLabel ? "".concat(selectedLabel, ".") : '', " ").concat((_accessibleProps_arialabel = accessibleProps['aria-label']) !== null && _accessibleProps_arialabel !== void 0 ? _accessibleProps_arialabel : '', " Double tap to change"),
                    testID: testID ? "".concat(testID, "_touchable") : undefined
                }),
                textInputProps: {
                    importantForAccessibility: 'no',
                    pointerEvents: 'none'
                },
                touchableDoneProps: {
                    testID: testID ? "".concat(testID, "_doneButton") : undefined
                },
                placeholder: placeholder ? {
                    label: placeholder,
                    value: '',
                    color: '#9191A3'
                } : {},
                onValueChange: onChangeValue ? function(value, idx) {
                    var newValue = value === '' ? undefined : props.items[placeholder ? idx - 1 : idx].value;
                    // This callback runs if props.value changes triggering a change in the underlying component
                    // However, if props.value is already equal to the new value, there is no point in
                    // calling onChangeValue
                    if (newValue !== props.value) {
                        onChangeValue(newValue);
                    }
                } : noop,
                style: {
                    inputIOS: inputStyle,
                    inputAndroid: inputStyle,
                    iconContainer: _object_spread({
                        top: 2,
                        bottom: 0,
                        justifyContent: 'center',
                        right: 16
                    }, Platform.OS === 'web' ? {
                        pointerEvents: 'none'
                    } : undefined),
                    headlessAndroidPicker: Platform.OS === 'web' ? {
                        color: theme.color.gray100
                    } // eslint-disable-line
                     : undefined,
                    placeholder: {
                        color: '#9191A3'
                    }
                },
                Icon: /*#__PURE__*/ _jsx(Icon, {
                    "aria-label": undefined,
                    name: "caret-down",
                    size: 14,
                    color: theme.color.gray400
                })
            }));
        }
    });
}
